/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosError, AxiosInstance } from 'axios';
import { exponentialDelay } from 'axios-retry';
import { LuisConstants } from 'src/core/utils/LuisConstants';

/**
 * Creates a retry interceptor to retry a request
 * a number of tries before failing.
 *
 * @todo Remove this when axios-retry is enabled.
 * @param client
 */
export const createRetryInterceptor = (client: AxiosInstance) => {
    const maxRetries = 20;
    const retryCountPerRequest = new Map<string, number>();

    client.interceptors.response.use(null, async (error: AxiosError) => {
        if (!retryCountPerRequest.has(error.config.url)) {
            retryCountPerRequest.set(error.config.url, 1);
        }
        const retryCount = retryCountPerRequest.get(error.config.url);

        if (retryCount < maxRetries && error.response.status === Number(LuisConstants.HttpResponseCodes.RequestThrottled)) {
            retryCountPerRequest.set(error.config.url, retryCount + 1);

            return new Promise(resolve => {
                setTimeout(() => resolve(client(error.config)), exponentialDelay(retryCount));
            });
        }
        retryCountPerRequest.set(error.config.url, 1);

        return Promise.reject(error);
    });
};
